/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts
import BroilerLanding from "pages/BroilerLanding";
import FacilityLanding from "pages/FacilityConfig/facilitylanding";
import Dashboard from "layouts/dashboard";
import Tables from "layouts/tables";
import Services from "layouts/billing";
import Reports from "layouts/reports";
import Analytics from "layouts/analytics";
import RTL from "layouts/rtl";
import Notifications from "layouts/notifications";
import Profile from "layouts/profile";
import Administration from "layouts/administration";
import AdministrationCosts from "layouts/administration/costs";
import AdministrationFacilities from "layouts/administration/facilities";
import AdministrationUsers from "layouts/administration/users";
import Transactions from "layouts/transactions";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import LogoutHandler from "pages/LogoutHandler";
import ServicesIcon from "components/Icons/ServicesIcon";
import { Navigate } from "react-router-dom";

// @mui icons
import Icon from "@mui/material/Icon";

const routes = [
  {
    type: "route",
    name: "BroilerLanding",
    key: "broiler-landing",
    route: "/facilities",
    component: <BroilerLanding />,
    icon: <Icon>home</Icon>, // Optional
  },
  {
    type: "route",
    name: "Facility Landing",
    key: "production-records",
    route: "/production-records",
    component: <FacilityLanding />,
  },
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
    href: "",
    onClick: () => {
      const STORAGE_KEY = "facilityLandingSelections";
      try {
        const storedRaw = localStorage.getItem(STORAGE_KEY);
        if (storedRaw) {
          const stored = JSON.parse(storedRaw);
          const rawFacilityId = stored?.facilityId;
          const normalizedFacilityId =
            rawFacilityId === undefined || rawFacilityId === null
              ? ""
              : String(rawFacilityId).trim();
          if (normalizedFacilityId) {
            return `/dashboard?facility=${encodeURIComponent(normalizedFacilityId)}`;
          }
        }
      } catch (error) {
        console.warn("Failed to read stored facility selection", error);
      }

      try {
        const broilers = JSON.parse(localStorage.getItem("broilers") || "[]");
        const firstValid = Array.isArray(broilers)
          ? broilers.find((entry) => entry && Object.keys(entry).length > 0)
          : null;
        const fallbackFacility =
          firstValid?.broilerID ??
          firstValid?.broilerId ??
          firstValid?.BroilerID ??
          firstValid?.facilityID ??
          firstValid?.facilityId ??
          firstValid?.id ??
          firstValid?.ID ??
          "";
        if (fallbackFacility) {
          return `/dashboard?facility=${encodeURIComponent(fallbackFacility)}`;
        }
      } catch (error) {
        console.warn("Failed to resolve fallback facility", error);
      }

      return "/dashboard";
    },
  },
  {
    type: "collapse",
    name: "Production",
    key: "facilities",
    icon: <Icon fontSize="small">table_view</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Records",
        key: "production-records-menu",
        icon: <Icon fontSize="small">assignment</Icon>,
        route: "/production-records",
        component: <FacilityLanding />,
      },
      {
        type: "collapse",
        name: "Transactions",
        key: "transactions",
        icon: <Icon fontSize="small">swap_horiz</Icon>,
        route: "/transactions",
        component: <Transactions />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Reports",
    key: "reports",
    icon: <Icon fontSize="small">bar_chart</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Facilities",
        key: "reports-facilities",
        icon: <Icon fontSize="small">business</Icon>,
        route: "/reports/facilities",
        component: <Reports />,
      },
      {
        type: "collapse",
        name: "Summary",
        key: "reports-summary",
        icon: <Icon fontSize="small">summarize</Icon>,
        route: "/reports/summary",
        component: <Analytics />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Analytics",
    key: "analytics",
    icon: <Icon fontSize="small">insights</Icon>,
    route: "/analytics",
    component: <Analytics />,
  },
  {
    type: "route",
    name: "Reports Redirect",
    key: "reports-redirect",
    route: "/reports",
    component: <Navigate to="/reports/facilities" replace />,
  },
  {
    type: "collapse",
    name: "Services",
    key: "services",
    icon: <ServicesIcon fontSize="small" />,
    route: "/services",
    component: <Services />,
  },
  /*{
    type: "collapse",
    name: "RTL",
    key: "rtl",
    icon: <Icon fontSize="small">format_textdirection_r_to_l</Icon>,
    route: "/rtl",
    component: <RTL />,
  },*/
  {
    type: "collapse",
    name: "Notifications",
    key: "notifications",
    icon: <Icon fontSize="small">notifications</Icon>,
    route: "/notifications",
    component: <Notifications />,
  },
  {
    type: "collapse",
    name: "Profile",
    key: "profile",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/profile",
    component: <Profile />,
  },
  {
    type: "collapse",
    name: "Administration",
    key: "administration",
    icon: <Icon fontSize="small">admin_panel_settings</Icon>,
    route: "/administration",
    component: <Administration />,
    visibleForUserTypeIDs: [1],
  },
  {
    type: "route",
    name: "Administration Users",
    key: "administration-users",
    route: "/administration/users",
    component: <AdministrationUsers />,
    visibleForUserTypeIDs: [1],
  },
  {
    type: "route",
    name: "Administration Costs",
    key: "administration-costs",
    route: "/administration/costs",
    component: <AdministrationCosts />,
    visibleForUserTypeIDs: [1],
  },
  {
    type: "route",
    name: "Administration Facilities",
    key: "administration-facilities",
    route: "/administration/facilities",
    component: <AdministrationFacilities />,
    visibleForUserTypeIDs: [1],
  },
  {
    type: "collapse",
    name: "Logout",
    key: "logout",
    icon: <Icon fontSize="small">logout</Icon>,
    route: "/logout",
    component: <LogoutHandler />,
  },
];

export default routes;
